/**
 * MusicPlayerSheet - ミニプレイヤーから開くフルスクリーンプレイヤー
 *
 * 実際の再生は `@/lib/music-audio-engine` が useMusicStore を購読して行う。
 */

import { ListMusic, MessageSquareQuote, Pause, Play, SkipBack, SkipForward, Star } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { AuthImage } from '@/components/ui/auth-image'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { RangeSlider } from '@/components/ui/slider'
import { formatTime } from '@/lib/cue-parser'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import { useMusicStore } from '@/store/useMusicStore'

import { ACCENT_COLOR } from './constants'
import { MusicLyricsPanel } from './lyrics-panel'
import { MarqueeText } from './marquee-text'
import { MusicQueuePanel } from './queue-panel'
import { MusicStemPanel, MusicStemToggle } from './stem-mixer'
import { MusicTrackMenu } from './track-menu'

type PlayerTab = 'player' | 'lyrics' | 'queue' | 'stem'

export const MusicPlayerSheet = () => {
  const [tab, setTab] = useState<PlayerTab>('player')
  const navigate = useNavigate()

  const isPlayerOpen = useMusicStore((s) => s.isPlayerOpen)
  const setPlayerOpen = useMusicStore((s) => s.setPlayerOpen)
  const track = useMusicStore((s) => s.queue.at(s.currentIndex))
  const isPlaying = useMusicStore((s) => s.isPlaying)
  const currentTime = useMusicStore((s) => s.currentTime)
  const actualDuration = useMusicStore((s) => s.duration)
  const isFavorite = useMusicStore((s) => (track ? s.favoriteEpisodeIds.includes(track.episodeId) : false))
  const togglePlay = useMusicStore((s) => s.togglePlay)
  const playNext = useMusicStore((s) => s.playNext)
  const playPrev = useMusicStore((s) => s.playPrev)
  const seekTo = useMusicStore((s) => s.seekTo)
  const toggleFavorite = useMusicStore((s) => s.toggleFavorite)

  // 実ファイルの長さを優先し、未取得の間だけ DB の値を使う
  const duration = actualDuration || (track?.duration ?? 0)

  // 左右の時間表示が同じ瞬間に切り替わるよう、整数化した経過秒から残りを算出する
  const elapsedSeconds = Math.floor(currentTime)
  const remainingSeconds = Math.max(0, Math.floor(duration) - elapsedSeconds)

  /** アーティスト名。タグIDがあればアーティスト画面へ遷移できるようにする */
  const artistLabel = (className: string) => {
    if (!track?.artist) return null
    if (!track.artistTagId) return <div className={cn('truncate text-muted-foreground', className)}>{track.artist}</div>

    return (
      <button
        type="button"
        onClick={() => {
          setPlayerOpen(false)
          navigate(PATHS.MUSIC.ARTIST_DETAIL.replace(':tag_id', String(track.artistTagId)))
        }}
        className={cn('block max-w-full truncate border-none p-0 text-left text-muted-foreground', className)}
      >
        {track.artist}
      </button>
    )
  }

  const trackActions = track && (
    <>
      <button
        type="button"
        aria-label={isFavorite ? 'お気に入りから外す' : 'お気に入りに追加'}
        aria-pressed={isFavorite}
        onClick={() => toggleFavorite(track.episodeId)}
        className="flex size-9 shrink-0 items-center justify-center rounded-full border-none bg-white/10"
        style={isFavorite ? { color: ACCENT_COLOR } : undefined}
      >
        <Star size={18} fill={isFavorite ? ACCENT_COLOR : 'none'} />
      </button>
      <MusicTrackMenu
        track={track}
        iconSize={18}
        className="flex size-9 items-center justify-center rounded-full bg-white/10 p-0 text-foreground"
      />
    </>
  )

  const artwork = (size: 'large' | 'small') => (
    <div
      className={cn(
        'shrink-0 overflow-hidden bg-white/10',
        size === 'large' ? 'mx-auto aspect-square w-[min(78vw,40vh)] rounded-lg shadow-2xl' : 'size-12 rounded-sm',
      )}
    >
      {track?.thumbnail ? (
        <AuthImage
          src={track.thumbnail}
          alt={track.title}
          lazy={false}
          className="size-full object-cover"
          cacheKey={`music-album-${track.productId}`}
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <ListMusic size={size === 'large' ? 48 : 20} className="text-muted-foreground" />
        </div>
      )}
    </div>
  )

  const compactHeader = track && (
    <div className="flex items-center gap-3">
      {artwork('small')}
      <div className="min-w-0 flex-1">
        <MarqueeText text={track.title} className="font-semibold" />
        {artistLabel('text-sm')}
      </div>
      {trackActions}
    </div>
  )

  // シークバー以下はスクロール領域の外に出し、シート下端に固定する
  // 横向き（short）では縦を歌詞に譲るため、3段を1行へ畳む
  const footer = track && (
    <div className="flex flex-col gap-4 px-6 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] short:flex-row short:items-center short:gap-4 short:px-4 short:pt-1 short:pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <div className="space-y-1 short:order-2 short:min-w-0 short:flex-1">
        <RangeSlider
          value={[Math.min(currentTime, duration || 1)]}
          onValueChange={([value]) => seekTo(value)}
          min={0}
          max={duration || 1}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatTime(elapsedSeconds)}</span>
          <span>-{formatTime(remainingSeconds)}</span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-10 short:order-1 short:shrink-0 short:gap-5">
        <button type="button" aria-label="前の曲" onClick={playPrev} className="border-none">
          <SkipBack size={30} fill="currentColor" className="short:size-6" />
        </button>
        <button type="button" aria-label={isPlaying ? '一時停止' : '再生'} onClick={togglePlay} className="border-none">
          {isPlaying ? (
            <Pause size={40} fill="currentColor" className="short:size-8" />
          ) : (
            <Play size={40} fill="currentColor" className="short:size-8" />
          )}
        </button>
        <button type="button" aria-label="次の曲" onClick={playNext} className="border-none">
          <SkipForward size={30} fill="currentColor" className="short:size-6" />
        </button>
      </div>

      <div className="flex items-center justify-around pt-1 short:order-3 short:shrink-0 short:gap-2 short:pt-0">
        <button
          type="button"
          aria-label="歌詞"
          aria-pressed={tab === 'lyrics'}
          onClick={() => setTab(tab === 'lyrics' ? 'player' : 'lyrics')}
          className={cn('border-none p-2', tab !== 'lyrics' && 'text-muted-foreground')}
        >
          <MessageSquareQuote size={22} />
        </button>
        <MusicStemToggle active={tab === 'stem'} onClick={() => setTab(tab === 'stem' ? 'player' : 'stem')} />
        <button
          type="button"
          aria-label="再生キュー"
          aria-pressed={tab === 'queue'}
          onClick={() => setTab(tab === 'queue' ? 'player' : 'queue')}
          className={cn('border-none p-2', tab !== 'queue' && 'text-muted-foreground')}
        >
          <ListMusic size={22} />
        </button>
      </div>
    </div>
  )

  return (
    <>
      <BottomSheet
        title=""
        hideTitleBar
        open={isPlayerOpen}
        onOpenChange={setPlayerOpen}
        snapPoints={[1]}
        overlayColor="rgba(0,0,0,0.6)"
        contentClassName="h-full"
        footer={footer}
      >
        <div className="flex h-full flex-col px-6 short:px-4">
          {!track ? (
            <div className="py-16 text-center text-muted-foreground">再生中の曲はありません</div>
          ) : (
            <div className={cn('flex min-h-0 flex-1 flex-col gap-4 short:gap-2', tab === 'player' && 'justify-center')}>
              {tab === 'player' && (
                <>
                  {artwork('large')}
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <MarqueeText text={track.title} className="text-xl font-bold" />
                      {artistLabel('text-xl')}
                    </div>
                    {trackActions}
                  </div>
                </>
              )}

              {tab === 'lyrics' && (
                <>
                  {/* 横向きでは縦幅を歌詞に譲る。曲情報はプレイヤータブで確認できる */}
                  <div className="shrink-0 short:hidden">{compactHeader}</div>
                  <div className="min-h-0 flex-1 overflow-hidden">
                    <MusicLyricsPanel
                      episodeId={track.episodeId}
                      title={track.title}
                      currentTime={currentTime}
                      onSeek={seekTo}
                    />
                  </div>
                </>
              )}

              {tab === 'stem' && (
                <>
                  <div className="shrink-0 short:hidden">{compactHeader}</div>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <MusicStemPanel />
                  </div>
                </>
              )}

              {tab === 'queue' && (
                <div className="min-h-0 flex-1">
                  <MusicQueuePanel header={compactHeader} />
                </div>
              )}
            </div>
          )}
        </div>
      </BottomSheet>
    </>
  )
}
